import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardBody, Col, Container, Input, Label, Row, Alert, Spinner } from 'reactstrap';
import BreadCrumb from '../../../Components/Common/BreadCrumb';
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import Dropzone from "react-dropzone";
import api,{handleApiError} from '../../../helpers/api';

import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useTranslation } from 'react-i18next';
import {useDispatch} from "react-redux";

const customStyles = `
  .create-course-card {
    padding: 12px;
    max-width: 700px;
    margin: 0 auto;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }
  .create-course-card .form-control {
    border-radius: 4px;
    border: 1px solid #ced4da;
  }
  .create-course-card .form-control:focus {
    border-color: #0ab39c;
    box-shadow: 0 0 0 0.2rem rgba(10, 179, 156, 0.25);
  }
  .create-course-card .ck-editor__editable {
    min-height: 150px;
    border-radius: 4px;
    border: 1px solid #ced4da;
  }
  .create-course-card .ck-editor__editable:focus {
    border-color: #0ab39c;
    box-shadow: 0 0 0 0.2rem rgba(10, 179, 156, 0.25);
  }
  .create-course-card .dropzone {
    border: 2px dashed #0ab39c;
    border-radius: 4px;
    padding: 1rem;
    text-align: center;
    transition: background-color 0.3s ease;
  }
  .create-course-card .dropzone:hover {
    background-color: #f1f5f7;
  }
  .create-course-card .btn-success {
    background-color: #0ab39c;
    border-color: #0ab39c;
  }
  .create-course-card .btn-success:hover {
    background-color: #099885;
    border-color: #099885;
  }
  .create-course-card .btn-danger {
    background-color: #f06548;
    border-color: #f06548;
  }
  .create-course-card .btn-danger:hover {
    background-color: #e55a3e;
    border-color: #e55a3e;
  }
  .create-course-card .btn:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;

const CreateCourse = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    document.title = t("create_course.title");

    const [formData, setFormData] = useState({
        titre: '',
        description: '',
        lienZoom: '',
        duree: '',
    });
    const [selectedImage, setSelectedImage] = useState([]);
    const [selectedVideo, setSelectedVideo] = useState([]);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleDescriptionChange = (event, editor) => {
        setFormData(prev => ({ ...prev, description: editor.getData() }));
    };

    const handleImageAccepted = (files) => {
        const file = files[0];
        if (file) {
            Object.assign(file, {
                preview: URL.createObjectURL(file),
                formattedSize: formatBytes(file.size),
            });
            setSelectedImage([file]);
        }
    };

    const handleVideoAccepted = (files) => {
        const file = files[0];
        if (file) {
            Object.assign(file, {
                preview: URL.createObjectURL(file),
                formattedSize: formatBytes(file.size),
            });
            setSelectedVideo([file]);
        }
    };

    const formatBytes = (bytes, decimals = 2) => {
        if (bytes === 0) return t("create_course.zero_bytes");
        const k = 1024;
        const dm = decimals < 0 ? 0 : decimals;
        const sizes = [
            t("create_course.bytes"),
            t("create_course.kb"),
            t("create_course.mb"),
            t("create_course.gb"),
            t("create_course.tb"),
            t("create_course.pb"),
            t("create_course.eb"),
            t("create_course.zb"),
            t("create_course.yb")
        ];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
    };

    const handleSubmit = async () => {
        if (!formData.titre) {
            setError(t("create_course.error_title"));
            toast.error(t("create_course.error_title"));
            return;
        }
        if (formData.duree && (isNaN(formData.duree) || Number(formData.duree) < 0)) {
            setError(t("create_course.error_duration"));
            toast.error(t("create_course.error_duration"));
            return;
        }
        if (formData.lienZoom && !/^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w- ./?%&=]*)?$/.test(formData.lienZoom)) {
            setError(t("create_course.error_zoom"));
            toast.error(t("create_course.error_zoom"));
            return;
        }

        try {
            setLoading(true);
            setError(null);
            const data = new FormData();
            data.append('titre', formData.titre);
            data.append('description', formData.description || '');
            data.append('lienZoom', formData.lienZoom || '');
            data.append('duree', formData.duree ? Number(formData.duree) : '');
            if (selectedImage[0]) {
                data.append('image', selectedImage[0]);
            }
            if (selectedVideo[0]) {
                data.append('previewVideo', selectedVideo[0]);
            }

            const response = await api.post('/formation', data, {
                headers: { 'Content-Type': 'multipart/form-data' },
                withCredentials: true,
            });

            if (response.data.status === 'success') {
                toast.success(t("create_course.success_create"));
                setFormData({
                    titre: '',
                    description: '',
                    lienZoom: '',
                    duree: '',
                });
                setSelectedImage([]);
                setSelectedVideo([]);
                setError(null);
                setTimeout(() => navigate('/apps-courses-list'), 1000);
            } else {
                setError(t("create_course.error_create"));
                toast.error(t("create_course.error_create"));
            }
        } catch (err) {
            if (err.response.data?.status === 401 || err.response.data?.status === 403) {
                await handleApiError(err, dispatch, navigate);
            } else {
                setError(err.response?.data?.message || t("create_course.error_create"));
                toast.error(err.response?.data?.message || t("create_course.error_create"));
            }
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = () => {
        setFormData({
            titre: '',
            description: '',
            lienZoom: '',
            duree: '',
        });
        setSelectedImage([]);
        setSelectedVideo([]);
        toast.info(t("create_course.info_clear"));
    };

    return (
        <React.Fragment>
            <style>{customStyles}</style>
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb title={t("create_course.breadcrumb_title")} pageTitle={t("create_course.breadcrumb_page_title")} />
                    <ToastContainer closeButton={false} limit={1} />
                    {error && <Alert color="danger">{error}</Alert>}
                    <Row className="justify-content-center">
                        <Col lg={8} className="create-course-card">
                            <Card>
                                <CardBody>
                                    {loading ? (
                                        <div className="text-center py-4">
                                            <Spinner color="primary" style={{ width: '2rem', height: '2rem' }} />
                                            <p className="mt-2">{t("create_course.creating")}</p>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="mb-3">
                                                <Label className="form-label" htmlFor="course-title-input">{t("create_course.course_title_label")}</Label>
                                                <Input
                                                    type="text"
                                                    className="form-control"
                                                    id="course-title-input"
                                                    name="titre"
                                                    value={formData.titre}
                                                    onChange={handleInputChange}
                                                    placeholder={t("create_course.course_title_placeholder")}
                                                    disabled={loading}
                                                />
                                            </div>
                                            <div className="mb-3">
                                                <Label className="form-label" htmlFor="course-image">{t("create_course.cover_image_label")}</Label>
                                                <Dropzone
                                                    onDrop={handleImageAccepted}
                                                    accept="image/png,image/jpeg,image/webp"
                                                    maxFiles={1}
                                                    disabled={loading}
                                                >
                                                    {({ getRootProps, getInputProps }) => (
                                                        <div className="dropzone dz-clickable">
                                                            <div className="dz-message needsclick" {...getRootProps()}>
                                                                <div className="mb-3">
                                                                    <i className="display-4 text-muted ri-upload-cloud-2-fill" />
                                                                </div>
                                                                <h4>{t("create_course.drop_image")}</h4>
                                                            </div>
                                                            <input {...getInputProps()} />
                                                        </div>
                                                    )}
                                                </Dropzone>
                                                <ul className="list-unstyled mb-0" id="image-preview">
                                                    {selectedImage.map((f, i) => (
                                                        <Card
                                                            className="mt-1 mb-0 shadow-none border dz-processing dz-image-preview dz-success dz-complete"
                                                            key={i + "-image"}
                                                        >
                                                            <div className="p-2">
                                                                <Row className="align-items-center">
                                                                    <Col className="col-auto">
                                                                        <img
                                                                            data-dz-thumbnail=""
                                                                            height="80"
                                                                            className="avatar-sm rounded bg-light"
                                                                            alt={f.name}
                                                                            src={f.preview}
                                                                        />
                                                                    </Col>
                                                                    <Col>
                                                                        <Link to="#" className="text-muted font-weight-bold">
                                                                            {f.name}
                                                                        </Link>
                                                                        <p className="mb-0">
                                                                            <strong>{f.formattedSize}</strong>
                                                                        </p>
                                                                    </Col>
                                                                </Row>
                                                            </div>
                                                        </Card>
                                                    ))}
                                                </ul>
                                            </div>
                                            <div className="mb-3">
                                                <Label className="form-label" htmlFor="course-preview-video">{t("create_course.preview_video_label")}</Label>
                                                <Dropzone
                                                    onDrop={handleVideoAccepted}
                                                    accept="video/mp4,video/mov,video/avi"
                                                    maxFiles={1}
                                                    disabled={loading}
                                                >
                                                    {({ getRootProps, getInputProps }) => (
                                                        <div className="dropzone dz-clickable">
                                                            <div className="dz-message needsclick" {...getRootProps()}>
                                                                <div className="mb-3">
                                                                    <i className="display-4 text-muted ri-upload-cloud-2-fill" />
                                                                </div>
                                                                <h4>{t("create_course.drop_video")}</h4>
                                                            </div>
                                                            <input {...getInputProps()} />
                                                        </div>
                                                    )}
                                                </Dropzone>
                                                <ul className="list-unstyled mb-0" id="video-preview">
                                                    {selectedVideo.map((f, i) => (
                                                        <Card
                                                            className="mt-1 mb-0 shadow-none border dz-processing dz-image-preview dz-success dz-complete"
                                                            key={i + "-video"}
                                                        >
                                                            <div className="p-2">
                                                                <Row className="align-items-center">
                                                                    <Col className="col-auto">
                                                                        <video
                                                                            height="80"
                                                                            className="avatar-sm rounded bg-light"
                                                                            controls
                                                                            disabled={loading}
                                                                        >
                                                                            <source src={f.preview} type={f.type} />
                                                                        </video>
                                                                    </Col>
                                                                    <Col>
                                                                        <Link to="#" className="text-muted font-weight-bold">
                                                                            {f.name}
                                                                        </Link>
                                                                        <p className="mb-0">
                                                                            <strong>{f.formattedSize}</strong>
                                                                        </p>
                                                                    </Col>
                                                                </Row>
                                                            </div>
                                                        </Card>
                                                    ))}
                                                </ul>
                                            </div>
                                            <div className="mb-3">
                                                <Label className="form-label" htmlFor="course-zoom-link">{t("create_course.zoom_link_label")}</Label>
                                                <Input
                                                    type="text"
                                                    className="form-control"
                                                    id="course-zoom-link"
                                                    name="lienZoom"
                                                    value={formData.lienZoom}
                                                    onChange={handleInputChange}
                                                    placeholder={t("create_course.zoom_link_placeholder")}
                                                    disabled={loading}
                                                />
                                            </div>
                                            <div className="mb-3">
                                                <Label className="form-label" htmlFor="course-duration">{t("create_course.duration_label")}</Label>
                                                <Input
                                                    type="number"
                                                    className="form-control"
                                                    id="course-duration"
                                                    name="duree"
                                                    value={formData.duree}
                                                    onChange={handleInputChange}
                                                    placeholder={t("create_course.duration_placeholder")}
                                                    disabled={loading}
                                                />
                                            </div>
                                            <div className="mb-3">
                                                <Label className="form-label">{t("create_course.description_label")}</Label>
                                                <CKEditor
                                                    editor={ClassicEditor}
                                                    data={formData.description}
                                                    onChange={handleDescriptionChange}
                                                    disabled={loading}
                                                />
                                            </div>
                                        </>
                                    )}
                                </CardBody>
                            </Card>
                            <div className="text-end mb-4">
                                <button
                                    type="button"
                                    className="btn btn-danger w-sm me-1"
                                    onClick={handleDelete}
                                    disabled={loading}
                                >
                                    {t("create_course.clear_button")}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-success w-sm"
                                    onClick={handleSubmit}
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <>
                                            <Spinner size="sm" className="me-2" />
                                            {t("create_course.creating")}
                                        </>
                                    ) : (
                                        t("create_course.create_button")
                                    )}
                                </button>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </div>
        </React.Fragment>
    );
};

export default CreateCourse;